// The Tax Savings Appeal Summary — the Property Executive Summary's second Excel layout (step 8 part 5b; Philip
// 2026-10-22, docs/reports-step8-an-vs-exec.md). Debbie's AN workbook, column for column: one sheet per year, then
// "Summary <first>-<last>".
//
// ⛔ HER MATH, NOT THE EXECUTIVE SUMMARY'S. Market values × an effective rate (the year's taxes ÷ final market), with
// live formulas so she can correct a figure in Excel and the row follows:
//   J Value Reduction = H − I · L Effective Tax Rate = K / 100 · M Proposed Tax = H × L · N Final Tax = I × L
//   O Savings = M − N.
// So a row's Savings here differs from the Standard layout's (taxable × the bill's rates) — on purpose.
//
// ⛔ A YEAR WITH NO TAXES IN WIGLEY HAS NO RATE: K, L, M, N and O are left BLANK and the row says why in the Note
// column — never a silent $0 (Philip, decision C).
// ⛔ Rows the options leave out keep their figures and say so in Note: a Non-NTRG row with "Non-NTRG savings: Leave
// out" shows $0 savings; an undecided row with "Pending savings: Pending" shows none.
// ⛔ Not reproducible with this Excel library (the free SheetJS build): her bold title and blue header fill. Number
// formats, column widths, formulas and the layout are hers.
import * as XLSX from 'xlsx';
import { APPEAL_STATUS } from '../constants/statusCodes';
import { toNumber, PENDING, rawValue } from './format';
import { levelNameFor } from '../utils/levelNames';

const ACCOUNTING = '_("$"* #,##0.00_);_("$"* \\(#,##0.00\\);_("$"* "-"??_);_(@_)';
const RATE = '#,##0.000000';
const HEADERS = ['SysYear', 'LocationCode', 'Account', 'Prop', 'Address1', 'Assessor', 'State', 'ProposedMarket', 'MarketValue',
  'Value Reduction', 'EffectiveTaxRate', 'Effective Tax Rate', 'Proposed Tax', 'Final Tax', 'Savings', 'ValueStatus', 'ValueType'];
const WIDTHS = [8, 23, 25, 50, 48, 19, 6, 18, 18, 18, 16, 18, 18, 18, 15, 17, 19];
const HEADER_ROW = 7;          // 1-based, as hers
const FIRST_DATA_ROW = 8;

// Her ValueStatus, from Wigley's status, decision and the Non-NTRG tick (decisions D, E, L — this column only; stored
// statuses are unchanged). Appealed / Inv Pending / Docs Pending / Client Filed have no Wigley equivalent and read
// Decision Pending like an open appeal.
export function valueStatus(r) {
  if (r.non_ntrg) return 'Non NTRG Savings';
  const st = r.appeal_status;
  if (st === APPEAL_STATUS.withdrawn || r.decision === 'withdrawn') return 'Withdrawn';
  if (st === APPEAL_STATUS.denied || r.decision === 'denied') return 'Denied';
  if (st === APPEAL_STATUS.value_accepted) {
    if (!r.appeal_level_key || r.appeal_level_key === 'proposed') return 'Accepted';
    const p = toNumber(r.proposed_market), f = toNumber(r.final_market);
    return p != null && f != null && p - f > 0 ? 'Accepted' : 'Denied';
  }
  if (st === APPEAL_STATUS.under_review) return 'Under Review';
  if (st === APPEAL_STATUS.appeal_open) return 'Decision Pending';
  return '';
}

// Her ValueType: an added notice's type (Supplemental …), else the final level's name in that state (Level Names).
export function valueType(r, levelNames) {
  if (r.notice_type) return r.notice_type;
  if (!r.appeal_level_key || r.appeal_level_key === 'proposed') return 'Proposed';
  return levelNameFor(levelNames, r.state, r.appeal_level_key);
}

// The row fields this layout needs, on the report's rows (baseline years are not part of it).
export function taxSavingsRows(rows, { levelNames = [], filters = {} } = {}) {
  const own = (rows || []).filter((r) => !r.baseline);
  // The effective rate is the original notice's year: an added notice's row borrows it (its own tax is blank).
  const rateOf = (r) => {
    const t = toNumber(r.taxes), m = toNumber(r.final_market);
    return t != null && m != null && m > 0 ? (t / m) * 100 : null;
  };
  const yearRate = new Map(own.filter((r) => !r.notice_no).map((r) => [`${r.parcel_id}|${r.tax_year}`, rateOf(r)]));
  return own.map((r) => {
    const rate = r.notice_no ? yearRate.get(`${r.parcel_id}|${r.tax_year}`) ?? null : rateOf(r);
    const leftOut = r.non_ntrg && filters.nonNtrg !== 'include' ? 'nonntrg'
      : rawValue(r.savings) === PENDING && filters.projected !== 'include' ? 'pending' : null;
    const note = rate == null ? `No taxes in Wigley for ${r.tax_year} — rate and savings left blank`
      : leftOut === 'nonntrg' ? 'Non-NTRG — left out of savings'
      : leftOut === 'pending' ? 'Not decided — savings pending' : '';
    return { ...r, an_rate: rate, an_left_out: leftOut, an_note: note, value_status: valueStatus(r), value_type: valueType(r, levelNames) };
  });
}

const cell = (ws, ref, c) => { ws[ref] = c; };
const num = (v, z) => (v == null ? null : { t: 'n', v, ...(z ? { z } : {}) });
const str = (v) => (v == null || v === '' ? null : { t: 's', v: String(v) });
const fml = (f, z) => ({ t: 'n', f, ...(z ? { z } : {}) });
const put = (ws, col, row, c) => { if (c) cell(ws, `${col}${row}`, c); };

function yearSheet(year, rows, { clientName, asOf }) {
  const ws = {};
  const anyNote = rows.some((r) => r.an_note);
  put(ws, 'D', 2, str(clientName));
  put(ws, 'D', 3, str(`${year} Tax Savings Appeal Summary Report`));
  put(ws, 'D', 4, { t: 'd', v: asOf, z: 'd-mmm-yy' });
  [...HEADERS, ...(anyNote ? ['Note'] : [])].forEach((h, i) => put(ws, XLSX.utils.encode_col(i), HEADER_ROW, str(h)));
  rows.forEach((r, i) => {
    const n = FIRST_DATA_ROW + i;
    put(ws, 'A', n, num(Number(r.tax_year)));
    put(ws, 'B', n, str(r.location_code));
    put(ws, 'C', n, str(r.account_number));
    put(ws, 'D', n, str(r.property));
    put(ws, 'E', n, str(r.address));
    put(ws, 'F', n, str(r.assessor));
    put(ws, 'G', n, str(r.state));
    put(ws, 'H', n, num(toNumber(r.proposed_market), ACCOUNTING));
    put(ws, 'I', n, num(toNumber(r.final_market), ACCOUNTING));
    put(ws, 'J', n, fml(`H${n}-I${n}`, ACCOUNTING));
    if (r.an_rate != null) {
      put(ws, 'K', n, num(Math.round(r.an_rate * 1e6) / 1e6, RATE));
      put(ws, 'L', n, fml(`K${n}/100`, RATE));
      put(ws, 'M', n, fml(`H${n}*L${n}`, ACCOUNTING));
      put(ws, 'N', n, fml(`I${n}*L${n}`, ACCOUNTING));
      if (r.an_left_out === 'nonntrg') put(ws, 'O', n, num(0, ACCOUNTING));
      else if (r.an_left_out !== 'pending') put(ws, 'O', n, fml(`M${n}-N${n}`, ACCOUNTING));
    }
    put(ws, 'P', n, str(r.value_status));
    put(ws, 'Q', n, str(r.value_type));
    if (anyNote) put(ws, 'R', n, str(r.an_note));
  });
  const last = FIRST_DATA_ROW + rows.length - 1;
  const totalRow = last + 1;
  put(ws, 'F', totalRow, str('Totals'));
  if (rows.length) ['H', 'I', 'J', 'M', 'N', 'O'].forEach((c) => put(ws, c, totalRow, fml(`SUM(${c}${FIRST_DATA_ROW}:${c}${last})`, ACCOUNTING)));
  ws['!ref'] = `A1:${anyNote ? 'R' : 'Q'}${totalRow}`;
  ws['!cols'] = [...WIDTHS, ...(anyNote ? [44] : [])].map((wch) => ({ wch }));
  return { ws, totalRow };
}

// marks: { '2024': '*', '2026': '**' } · footnotes: ['An asterisk (*) …', …] — typed in the report's options.
function summarySheet(years, totalRows, { clientName, marks, footnotes }) {
  const ws = {};
  put(ws, 'A', 1, str(`${clientName} Summary Tax Savings`));
  put(ws, 'A', 2, str(years.length > 1 ? `${years[0]} - ${years[years.length - 1]}` : years[0]));
  put(ws, 'A', 5, str('Year'));
  put(ws, 'B', 5, str('  Tax Savings  '));
  years.forEach((y, i) => {
    const n = 6 + i;
    put(ws, 'A', n, num(Number(y)));
    put(ws, 'B', n, fml(`'${y}'!O${totalRows[y]}`, ACCOUNTING));
    put(ws, 'C', n, str(marks[y]));
  });
  const totalRow = 6 + years.length;
  put(ws, 'A', totalRow, str('Total'));
  put(ws, 'B', totalRow, fml(`SUM(B6:B${totalRow - 1})`, ACCOUNTING));
  const avgRow = totalRow + 2;
  put(ws, 'A', avgRow, str(`Current Annual Average (${years.length > 1 ? `${years[0]} - ${years[years.length - 1]}` : years[0]})`));
  put(ws, 'B', avgRow, fml(`B${totalRow}/${years.length}`, ACCOUNTING));
  footnotes.forEach((f, i) => put(ws, 'A', avgRow + 3 + i * 2, str(f)));
  ws['!ref'] = `A1:C${Math.max(avgRow + 1 + footnotes.length * 2, avgRow)}`;
  ws['!cols'] = [{ wch: 36 }, { wch: 18 }, { wch: 4 }];
  return ws;
}

// "2024=*, 2026=**" → { '2024': '*', '2026': '**' }
export const parseMarks = (text) => Object.fromEntries(String(text || '').split(/[,;\n]/)
  .map((p) => p.split('=').map((x) => x.trim())).filter(([y, m]) => /^\d{4}$/.test(y || '') && m));

// The workbook: { name, ws } descriptors for exportSheetsToXlsx.
// The report date, local midnight — a date, not a moment (never toISOString: it rolls the date west of Greenwich).
const today = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };

export function taxSavingsSheets(rows, { clientName = '', asOf = today(), marksText = '', footnotesText = '' } = {}) {
  const years = [...new Set(rows.map((r) => String(r.tax_year)))].sort();
  const totalRows = {};
  const sheets = years.map((y) => {
    const { ws, totalRow } = yearSheet(y, rows.filter((r) => String(r.tax_year) === y), { clientName, asOf });
    totalRows[y] = totalRow;
    return { name: y, ws };
  });
  if (!years.length) return sheets;
  const footnotes = String(footnotesText || '').split('\n').map((l) => l.trim()).filter(Boolean);
  return [...sheets, { name: `Summary ${years[0]}-${years[years.length - 1]}`, ws: summarySheet(years, totalRows, { clientName, marks: parseMarks(marksText), footnotes }) }];
}
